import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader, BookOpen, Info } from "lucide-react";
import ConsumerHeader from "./ConsumerHeader";
import PracticeAreaSelect from "./PracticeAreaSelect";
import ListingCard from "./ListingCard";
import Footer from "./Footer";
import SEO from "./SEO";
import { useTheme } from "../context/ThemeContext";
import { getPublicDirectoryListings } from "../services/directoryService";
import { matchPracticeArea } from "../utils/practiceAreaMatch";
import { US_STATES, STATE_STORAGE_KEY } from "../constants/directoryOptions";
import "./Directory.css";
import "./FindAttorney.css";

const PAGE_SIZE = 24;

export default function FindAttorney() {
  const { darkMode } = useTheme();

  const [usState, setUsState] = useState("");
  const [role, setRole] = useState("");
  const [practiceArea, setPracticeArea] = useState("");
  const [customArea, setCustomArea] = useState("");

  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  // Set when the free text resolved to a real category, so the results header
  // can explain why the visitor is seeing this particular set of attorneys.
  const [matchedNote, setMatchedNote] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchParams] = useSearchParams();

  // Pre-fill from the query string first — a state-scoped link should land on
  // that state — then fall back to the state the visitor chose on the question
  // page.
  useEffect(() => {
    const fromQuery = searchParams.get("state");

    if (fromQuery && US_STATES.includes(fromQuery)) {
      setUsState(fromQuery);
      return;
    }

    try {
      const saved = localStorage.getItem(STATE_STORAGE_KEY);
      if (saved && US_STATES.includes(saved)) setUsState(saved);
    } catch (e) {
      // Private browsing — nothing to restore.
    }
  }, [searchParams]);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    setError("");

    // What the visitor typed under "Other practice area" often belongs to a
    // category we already have ("wife filed for divorce" -> Family Law). When
    // it does, filter by that category rather than searching listing text for
    // the phrase; genuine Others still fall through to the free-text search.
    const typed = customArea.trim();
    const matchedArea = typed ? matchPracticeArea(typed) : "";
    const effectiveArea = practiceArea || matchedArea;

    // The note explains why this particular set of people came back, so it
    // belongs to the free-text box and only the free-text box.
    //
    // It used to require matchPracticeArea to have resolved a category, which
    // left it off exactly when it read as inconsistent: "divorce" maps to
    // Family Law and showed the note, while "Family" maps to nothing, falls
    // through to the free-text search, finds the same listings, and showed
    // none. Both are the free-text box doing its job. A dropdown choice is a
    // plain filter and never gets the note.
    const usedFreeText = !!typed && !practiceArea;

    try {
      const data = await getPublicDirectoryListings({
        state: usState,
        role,
        practiceArea: effectiveArea,
        // A genuine "Other" stays a practice-area search. Sending it as the
        // global `search` also matched names, cities and firm names, so a
        // visitor describing their problem got unrelated listings back.
        practiceAreaSearch: matchedArea ? "" : typed,
        page: 1,
        limit: PAGE_SIZE,
      });
      const found = data.listings || [];
      setListings(found);
      // The API reports the count under `pagination`, not at the top level.
      setTotal(data.pagination?.total ?? 0);
      // Nothing came back means there is no "listed below" to point at.
      setMatchedNote(usedFreeText && found.length > 0);
    } catch (err) {
      setError(err.message || "We could not load the directory just now.");
      setListings([]);
      setTotal(0);
      setMatchedNote(false);
    } finally {
      setLoading(false);
    }
  }, [usState, role, practiceArea, customArea]);

  // Debounced so typing in the free-text field does not fire a request per key.
  useEffect(() => {
    const t = setTimeout(fetchListings, 300);
    return () => clearTimeout(t);
  }, [fetchListings]);

  const onStateChange = (value) => {
    setUsState(value);
    try {
      localStorage.setItem(STATE_STORAGE_KEY, value);
    } catch (e) {
      // no-op
    }
  };

  const scope = usState ? `${usState} · statewide` : "All states";

  return (
    <>
      <SEO
        title="Find an Attorney or Paralegal"
        description="Search legal professionals in your state who work on your type of legal issue."
        path="/find-attorney"
      />
      <ConsumerHeader />

      <div className={`directory-container ${darkMode ? "dark" : ""}`}>
        <main className="fa-page">
          <section className="fa-hero">
            <h1>Find an Attorney or Paralegal</h1>
            <p>
              Search legal professionals in your state who work on your type of
              legal issue.
            </p>
          </section>

          <section className="fa-filters-shell">
            <div className="fa-filters">
              <div>
                <label htmlFor="fa-state">State</label>
                <select
                  id="fa-state"
                  value={usState}
                  onChange={(e) => onStateChange(e.target.value)}
                >
                  <option value="">All states</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <p className="fa-note">Results are statewide.</p>

                <label htmlFor="fa-role">Role</label>
                <select
                  id="fa-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="Attorney">Attorney</option>
                  <option value="Paralegal">Paralegal</option>
                </select>
              </div>

              <div>
                <label htmlFor="fa-area">Practice area</label>
                <PracticeAreaSelect
                  id="fa-area"
                  value={practiceArea}
                  onChange={setPracticeArea}
                />

                <label htmlFor="fa-custom">Other practice area</label>
                <p className="fa-note fa-note-tight">
                  Don't see your practice area? Type it here.
                </p>
                <input
                  id="fa-custom"
                  type="text"
                  value={customArea}
                  onChange={(e) => setCustomArea(e.target.value)}
                  placeholder="e.g., Class Actions, Civil Rights, Landlord-Tenant"
                />
              </div>
            </div>
          </section>

          {matchedNote && !loading && (
            <div className="fa-match-note">
              <Info size={16} />
              <span>
                Legal professionals who may handle the type of legal issue you
                described are listed below.
              </span>
            </div>
          )}

          <div className="fa-results-head">
            <h2>
              {loading
                ? "Searching…"
                : `${total} ${total === 1 ? "listing" : "listings"}`}
            </h2>
            <span className="fa-scope">{scope}</span>
          </div>

          {error && <div className="fa-error">{error}</div>}

          {loading ? (
            <div className="fa-loading">
              <Loader size={22} className="fa-spin" />
              <p>Loading legal professionals…</p>
            </div>
          ) : listings.length === 0 ? (
            <div className="empty-state">
              <BookOpen size={52} style={{ opacity: 0.4, marginBottom: "1rem" }} />
              <p>
                No listings match these filters yet. Try a different state or
                practice area.
              </p>
            </div>
          ) : (
            <div className="listing-grid">
              {listings.map((item) => (
                <ListingCard listing={item} key={item._id} />
              ))}
            </div>
          )}

          <p className="fa-disclaimer">
            LawFusion does not recommend or endorse any attorney. Listings are
            submitted by legal professionals and are not a check of bar license
            or credentials.
          </p>
        </main>
      </div>

      <Footer audience="consumer" />
    </>
  );
}
